import { SupabaseClient } from '@supabase/supabase-js';

// TypeScript interface for the data returned by the RPC function
export interface MarketProjections {
  location_quotient: number;
  market_status: 'Undersupplied' | 'Balanced' | 'Oversupplied' | 'Unknown';
  estimated_monthly_fuel_volume: number;
  estimated_annual_fuel_volume: number;
}

// Service function to call the Supabase RPC
export async function getMarketProjections(
  supabase: SupabaseClient,
  stationObjectId: number
): Promise<{ success: boolean; data: MarketProjections | null; error: string | null }> {
  if (!stationObjectId) {
    return { success: false, data: null, error: 'Station OBJECTID is required.' };
  }

  try {
    // Calling the RPC function created in Supabase
    const { data, error } = await supabase.rpc('calculate_market_projections', {
      station_objectid: stationObjectId,
    });

    if (error) {
      console.error('Error calling market projections RPC:', error);
      throw new Error(error.message);
    }

    // The RPC returns an array, we expect a single object or an empty array
    if (!data || data.length === 0) {
      return { success: false, data: null, error: 'No projection data returned for the given station. It may not exist in the private dataset.' };
    }

    // The RPC returns an array with a single element
    const projections: MarketProjections = data[0];

    return { success: true, data: projections, error: null };

  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching market projections.';
    console.error(errorMessage);
    return { success: false, data: null, error: errorMessage };
  }
}

// Helper function to estimate annual revenue from projections
export function estimateAnnualRevenue(projections: MarketProjections): { estimatedAnnualRevenue: number; estimatedCStoreRevenue: number } {
  const averageFuelMargin = 0.30; // Industry average, can be refined
  const insideSalesRatio = 0.65; // Assumption: C-store sales are 65% of fuel revenue

  const estimatedFuelRevenue = projections.estimated_annual_fuel_volume * averageFuelMargin;
  const estimatedCStoreRevenue = estimatedFuelRevenue * insideSalesRatio;
  
  const estimatedAnnualRevenue = estimatedFuelRevenue + estimatedCStoreRevenue;

  return { estimatedAnnualRevenue, estimatedCStoreRevenue };
}
